import MeterCodes from '@/lib/es-bill-shared-js/MeterCodes';

const EnergyDataStatus = {
  COMPLETE: 'complete',
  INCOMPLETE: 'incomplete',
  NO_ANALYSIS: 'noAnalysis',
};

const ENERGY_SUM_DEVIATION_THRESHOLD = 1;

const BILLING_RELEVANT_METER_CODES = new Set([
  MeterCodes.memberProductionForCommunityMft,
  MeterCodes.producerProductionRemainderMft,
  MeterCodes.memberShareEffective,
]);

/**
 * @typedef {Object} DataAnalysisResultSum
 * @property {number} [energySumKwh]
 * @property {number} [energySumkWh]
 * @property {string} [meteringMethod]
 */

/**
 * @typedef {Object} BillrunDataAnalysisResult
 * @property {Array} [incorrectCountOfQhResults]
 * @property {Array} [uncleanMeteringPointsDetailResults]
 * @property {Array} [incorrectMeterCodesResults]
 * @property {DataAnalysisResultSum} [energySumConsumersCommunityAssigned]
 * @property {DataAnalysisResultSum} [energySumProducersCommunityAssigned]
 */

/**
 * @typedef {Object} EnergyDataInterpretationOptions
 * @property {boolean} [billingRelevantOnly]
 */

/**
 * @typedef {Object} EnergyDataInterpretation
 * @property {'complete'|'incomplete'|'noAnalysis'} dsoStatus
 * @property {number|null} energySumDeviation
 * @property {boolean} hasSumDeviation
 * @property {boolean} isBillable
 * @property {boolean} showSumDeviationBadge
 * @property {string} sumDeviationDisplay
 * @property {number} incorrectQhCount
 * @property {number} l3Count
 * @property {number} surplusCount
 * @property {number} missingDataCount
 */

/**
 * @param {unknown} value
 * @returns {number}
 */
function countArrayItems(value) {
  return Array.isArray(value) ? value.length : 0;
}

/**
 * @param {unknown} code
 * @returns {boolean}
 */
function isBillingRelevantMeterCode(code) {
  return typeof code === 'string' && BILLING_RELEVANT_METER_CODES.has(code);
}

/**
 * @param {unknown} results
 * @returns {Array}
 */
function filterIncorrectCountOfQhResults(results) {
  if (!Array.isArray(results)) {
    return [];
  }
  return results.filter((entry) => isBillingRelevantMeterCode(entry?.meterCode));
}

/**
 * @param {unknown} results
 * @returns {Array}
 */
function filterUncleanMeteringPointsDetailResults(results) {
  if (!Array.isArray(results)) {
    return [];
  }
  return results.filter((entry) => isBillingRelevantMeterCode(entry?.meterCode));
}

/**
 * @param {unknown} meterCodes
 * @returns {string[]}
 */
function filterMeterCodeList(meterCodes) {
  if (!Array.isArray(meterCodes)) {
    return [];
  }
  return meterCodes.filter((code) => isBillingRelevantMeterCode(code));
}

/**
 * @param {unknown} results
 * @returns {Array}
 */
function filterIncorrectMeterCodesResults(results) {
  if (!Array.isArray(results)) {
    return [];
  }
  return results.reduce((filtered, entry) => {
    const meterCodesMissing = filterMeterCodeList(entry?.meterCodesMissing);
    const meterCodesSurplus = filterMeterCodeList(entry?.meterCodesSurplus);
    if (meterCodesMissing.length === 0 && meterCodesSurplus.length === 0) {
      return filtered;
    }
    filtered.push({
      ...entry,
      meterCodesMissing,
      meterCodesSurplus,
    });
    return filtered;
  }, []);
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function parseEnergySum(value) {
  let raw = value;
  if (value != null && typeof value === 'object') {
    raw = value.energySumKwh ?? value.energySumkWh;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * @param {EnergyDataInterpretationOptions|undefined} options
 * @returns {boolean}
 */
function isBillingRelevantOnly(options) {
  return options?.billingRelevantOnly !== false;
}

/**
 * @typedef {Object} FilteredIssueResults
 * @property {Array} incorrectCountOfQhResults
 * @property {Array} uncleanMeteringPointsDetailResults
 * @property {Array} incorrectMeterCodesResults
 */

/**
 * @param {BillrunDataAnalysisResult|null|undefined} dataAnalysisResult
 * @param {EnergyDataInterpretationOptions} [options]
 * @returns {FilteredIssueResults}
 */
export function getFilteredIssueResults(dataAnalysisResult, options) {
  if (dataAnalysisResult == null) {
    return {
      incorrectCountOfQhResults: [],
      uncleanMeteringPointsDetailResults: [],
      incorrectMeterCodesResults: [],
    };
  }

  let incorrectCountOfQhResults = dataAnalysisResult.incorrectCountOfQhResults;
  let uncleanMeteringPointsDetailResults = dataAnalysisResult.uncleanMeteringPointsDetailResults;
  let incorrectMeterCodesResults = dataAnalysisResult.incorrectMeterCodesResults;

  if (isBillingRelevantOnly(options)) {
    incorrectCountOfQhResults = filterIncorrectCountOfQhResults(incorrectCountOfQhResults);
    uncleanMeteringPointsDetailResults = filterUncleanMeteringPointsDetailResults(
      uncleanMeteringPointsDetailResults
    );
    incorrectMeterCodesResults = filterIncorrectMeterCodesResults(incorrectMeterCodesResults);
  }

  return {
    incorrectCountOfQhResults: Array.isArray(incorrectCountOfQhResults)
      ? incorrectCountOfQhResults
      : [],
    uncleanMeteringPointsDetailResults: Array.isArray(uncleanMeteringPointsDetailResults)
      ? uncleanMeteringPointsDetailResults
      : [],
    incorrectMeterCodesResults: Array.isArray(incorrectMeterCodesResults)
      ? incorrectMeterCodesResults
      : [],
  };
}

/**
 * Count distinct metering points; same meter point on multiple days counts once.
 *
 * @param {Array} entries
 * @param {(entry: unknown) => boolean} [hasIssue]
 * @returns {number}
 */
function countUniqueMeterPoints(entries, hasIssue = () => true) {
  const seen = new Set();
  for (const entry of entries) {
    const mp = entry?.meteringPoint;
    if (typeof mp === 'string' && mp.length > 0 && hasIssue(entry)) {
      seen.add(mp);
    }
  }
  return seen.size;
}

/**
 * @param {FilteredIssueResults} filtered
 * @returns {Pick<EnergyDataInterpretation, 'incorrectQhCount' | 'l3Count' | 'surplusCount' | 'missingDataCount'>}
 */
function getBillrunIssueCounts(filtered) {
  return {
    incorrectQhCount: countUniqueMeterPoints(filtered.incorrectCountOfQhResults),
    l3Count: countUniqueMeterPoints(filtered.uncleanMeteringPointsDetailResults),
    surplusCount: countUniqueMeterPoints(
      filtered.incorrectMeterCodesResults,
      (entry) => Array.isArray(entry?.meterCodesSurplus) && entry.meterCodesSurplus.length > 0
    ),
    missingDataCount: countUniqueMeterPoints(
      filtered.incorrectMeterCodesResults,
      (entry) => Array.isArray(entry?.meterCodesMissing) && entry.meterCodesMissing.length > 0
    ),
  };
}

/**
 * Dso Status Complete means we have complete energy data for each mpm and no L3 measurements.
 *
 * @param {BillrunDataAnalysisResult|null|undefined} dataAnalysisResult
 * @param {EnergyDataInterpretationOptions} [options]
 * @returns {Pick<EnergyDataInterpretation, 'dsoStatus'>}
 */
function getDsoStatus(dataAnalysisResult, options) {
  if (dataAnalysisResult == null) {
    return {
      dsoStatus: EnergyDataStatus.NO_ANALYSIS,
    };
  }

  const filtered = getFilteredIssueResults(dataAnalysisResult, options);
  const incorrectCountOfQh = countArrayItems(filtered.incorrectCountOfQhResults);
  const uncleanMeteringPointsDetail = countArrayItems(filtered.uncleanMeteringPointsDetailResults);
  const incorrectMeterCodes = countArrayItems(filtered.incorrectMeterCodesResults);
  const total = incorrectCountOfQh + uncleanMeteringPointsDetail + incorrectMeterCodes;

  return {
    dsoStatus:
      total === 0 ? EnergyDataStatus.COMPLETE : EnergyDataStatus.INCOMPLETE,
  };
}

/**
 * Energy Sum Deviation means the difference between the energy sum of producers and consumers
 * is more than 1kWh.
 *
 * energySumDeviation = consumers - producers
 * hasSumDeviation = true when |energySumDeviation| > 1, or when sums are missing
 *
 * @param {BillrunDataAnalysisResult|null|undefined} dataAnalysisResult
 * @returns {Pick<EnergyDataInterpretation, 'energySumDeviation' | 'hasSumDeviation'>}
 */
function calculateEnergySumDeviation(dataAnalysisResult) {
  if (dataAnalysisResult == null) {
    return {
      energySumDeviation: null,
      hasSumDeviation: true,
    };
  }

  const consumers = parseEnergySum(dataAnalysisResult.energySumConsumersCommunityAssigned);
  const producers = parseEnergySum(dataAnalysisResult.energySumProducersCommunityAssigned);

  if (consumers === null || producers === null) {
    return {
      energySumDeviation: null,
      hasSumDeviation: true,
    };
  }

  const energySumDeviation = consumers - producers;

  return {
    energySumDeviation,
    hasSumDeviation: Math.abs(energySumDeviation) > ENERGY_SUM_DEVIATION_THRESHOLD,
  };
}

/**
 * @param {number|null|undefined} energySumDeviation
 * @returns {string}
 */
function formatEnergySumDeviationDisplay(energySumDeviation) {
  if (energySumDeviation == null) return '—';
  const rounded = Math.sign(energySumDeviation) * Math.ceil(Math.abs(energySumDeviation));
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded} kWh`;
}

/**
 * Interpret a billrun data analysis result into an energy-data completeness status.
 *
 * @param {BillrunDataAnalysisResult|null|undefined} dataAnalysisResult
 * @param {EnergyDataInterpretationOptions} [options]
 * @returns {EnergyDataInterpretation}
 */
export function interpretBillrunEnergyDataAnalysis(dataAnalysisResult, options) {
  const filtered = getFilteredIssueResults(dataAnalysisResult, options);
  const { dsoStatus } = getDsoStatus(dataAnalysisResult, options);
  const { energySumDeviation, hasSumDeviation } = calculateEnergySumDeviation(dataAnalysisResult);
  const issueCounts = dataAnalysisResult == null
    ? {
        incorrectQhCount: 0,
        l3Count: 0,
        surplusCount: 0,
        missingDataCount: 0,
      }
    : getBillrunIssueCounts(filtered);

  return {
    dsoStatus,
    energySumDeviation,
    hasSumDeviation,
    isBillable: dsoStatus === EnergyDataStatus.COMPLETE && !hasSumDeviation,
    showSumDeviationBadge: dsoStatus === EnergyDataStatus.COMPLETE && hasSumDeviation,
    sumDeviationDisplay: formatEnergySumDeviationDisplay(energySumDeviation),
    ...issueCounts,
  };
}
